import { useCallback, useEffect, useRef, useState } from "react";

const SHARE_FIELDS = ["url", "text", "title"];

const extractUrlFromText = (value) => {
  if (!value || typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  try {
    const parsed = new URL(trimmed);
    return parsed.toString();
  } catch (_) {
    const match = trimmed.match(/https?:\/\/[^\s]+/i);
    if (match && match[0]) {
      return match[0].replace(/[)\],.]+$/, "");
    }
  }
  return undefined;
};

const normalizePayload = (raw) => {
  if (!raw) return null;
  const normalized = {};
  SHARE_FIELDS.forEach((field) => {
    if (raw[field] && typeof raw[field] === "string") {
      const val = raw[field].trim();
      if (val) normalized[field] = val;
    }
  });
  if (!normalized.url) {
    const fallbackUrl = extractUrlFromText(normalized.text) || extractUrlFromText(normalized.title);
    if (fallbackUrl) normalized.url = fallbackUrl;
  }
  return Object.keys(normalized).length ? normalized : null;
};

const payloadFromSearchParams = () => {
  try {
    const params = new URLSearchParams(window.location.search || "");
    return normalizePayload({
      url: params.get("url") || params.get("link") || params.get("sharedUrl"),
      text: params.get("text"),
      title: params.get("title") || params.get("subject"),
    });
  } catch (err) {
    console.warn("shareReceiver: params parsing failed", err);
    return null;
  }
};

const payloadFromLaunchParams = async (launchParams) => {
  if (!launchParams) return null;
  const payload = {};

  if (Array.isArray(launchParams.files)) {
    for (const handle of launchParams.files) {
      try {
        const file = await handle.getFile();
        const text = await file.text();
        const name = file?.name;
        console.log(`Fichier launchParams: ${name}`, text.substring(0, 200));
        if (name && SHARE_FIELDS.includes(name)) {
          payload[name] = text;
        } else if (!payload.url) {
          const maybeUrl = extractUrlFromText(text);
          if (maybeUrl) payload.url = maybeUrl;
        }
      } catch (err) {
        console.warn("shareReceiver: cannot read shared file", err);
      }
    }
  }

  if (launchParams.targetURL && !payload.url) {
    payload.url = launchParams.targetURL;
  }

  return normalizePayload(payload);
};

function ShareReceiver() {
  const [message, setMessage] = useState("En attente des informations partagées...");
  const [error, setError] = useState(null);
  const [rawPayload, setRawPayload] = useState(null);
  const [logs, setLogs] = useState([]);
  const [criticalError, setCriticalError] = useState(null);
  const redirectedRef = useRef(false);
  const payloadAttemptedRef = useRef(false);

  const addLog = useCallback((msg) => {
    console.log(msg);
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${msg}`]);
  }, []);

  // Capturer les erreurs globales
  useEffect(() => {
    const handleError = (event) => {
      const errorMsg = `ERREUR GLOBALE: ${event.error?.message || event.message}`;
      addLog(errorMsg);
      setCriticalError(errorMsg);
    };

    const handleUnhandledRejection = (event) => {
      const errorMsg = `PROMESSE REJETÉE: ${event.reason?.message || event.reason}`;
      addLog(errorMsg);
      setCriticalError(errorMsg);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    addLog("Composant ShareReceiver initialisé");

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [addLog]);

  const redirectToImporter = useCallback((url) => {
    if (!url || redirectedRef.current) return;
    redirectedRef.current = true;
    const destination = `/import?url=${encodeURIComponent(url)}`;
    setMessage("Redirection vers l'import automatique...");
    addLog(`Redirection vers: ${destination}`);
    setTimeout(() => {
      window.location.replace(destination);
    }, 500);
  }, [addLog]);

  const handlePayload = useCallback(
    (incoming, source = "unknown") => {
      addLog(`Payload reçu de: ${source}`);
      payloadAttemptedRef.current = true;
      setRawPayload(incoming);
      const payload = normalizePayload(incoming);
      
      if (!payload || !payload.url) {
        addLog("Aucune URL valide trouvée");
        setError("Aucune URL valide trouvée dans le partage.");
        setMessage("Impossible de continuer.");
        return;
      }
      
      addLog(`URL extraite: ${payload.url}`);
      if (window.umami) window.umami.track("shareReceiver-Forwarded");
      redirectToImporter(payload.url);
    },
    [redirectToImporter, addLog]
  );

  // Écouter les messages du Service Worker AVANT tout
  useEffect(() => {
    try {
      if (!("serviceWorker" in navigator)) {
        addLog("Service Worker non supporté");
        return undefined;
      }

      const handleServiceWorkerMessage = (event) => {
        try {
          const { data } = event || {};
          addLog(`Message SW reçu: ${JSON.stringify(data?.type)}`);
          if (!data || data.type !== "share-target") return;
          handlePayload(data.payload, "Service Worker");
        } catch (err) {
          addLog(`Erreur traitement message SW: ${err.message}`);
        }
      };

      // Écouter immédiatement
      navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);
      addLog("Listener Service Worker installé");

      // Vérifier si un SW est actif
      navigator.serviceWorker.ready
        .then(registration => {
          addLog(`Service Worker actif: ${registration.active?.state}`);
        })
        .catch(err => {
          addLog(`Erreur SW ready: ${err.message}`);
        });

      return () => {
        navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
      };
    } catch (err) {
      addLog(`Erreur initialisation SW: ${err.message}`);
      return undefined;
    }
  }, [handlePayload, addLog]);

  // LaunchQueue (pour PWA)
  useEffect(() => {
    let cancelled = false;
    
    const processLaunchParams = async (launchParams) => {
      try {
        addLog("LaunchParams reçu");
        const payload = await payloadFromLaunchParams(launchParams);
        if (!cancelled && payload) {
          handlePayload(payload, "LaunchQueue");
        } else if (!cancelled && !payload) {
          addLog("LaunchParams sans URL");
          payloadAttemptedRef.current = true;
          setMessage("Partage reçu mais aucune URL détectée.");
          setError("URL manquante.");
        }
      } catch (err) {
        addLog(`Erreur LaunchParams: ${err.message}`);
      }
    };

    try {
      if ("launchQueue" in window && typeof window.launchQueue?.setConsumer === "function") {
        addLog("LaunchQueue disponible");
        window.launchQueue.setConsumer((launchParams) => {
          processLaunchParams(launchParams);
        });
      } else {
        addLog("LaunchQueue non disponible");
      }
    } catch (err) {
      addLog(`Erreur initialisation LaunchQueue: ${err.message}`);
    }

    return () => {
      cancelled = true;
    };
  }, [handlePayload, addLog]);

  // Query params (fallback)
  useEffect(() => {
    try {
      const fallback = payloadFromSearchParams();
      if (fallback) {
        addLog("Données trouvées dans les query params");
        handlePayload(fallback, "Query Params");
      } else {
        addLog("Pas de données dans les query params");
      }
    } catch (err) {
      addLog(`Erreur query params: ${err.message}`);
    }
  }, [handlePayload, addLog]);

  // Timeout
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (!payloadAttemptedRef.current && !redirectedRef.current) {
        addLog("Timeout: aucune donnée reçue");
        setError("Partage introuvable.");
        setMessage("Impossible de récupérer les informations partagées.");
      }
    }, 8000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [addLog]);

  return (
    <div className="share-wrapper" style={{ padding: "2rem", textAlign: "center" }}>
      <h2>Redirection du partage</h2>
      <p>{message}</p>
      
      {error && (
        <div className="ui-error" style={{ marginTop: "1rem", color: "red", fontWeight: "bold" }}>
          {error}
        </div>
      )}
      
      {!error && (
        <p style={{ marginTop: "1rem", fontSize: "0.9rem" }}>
          Vous serez redirigé automatiquement vers l'écran d'import Listaki.
        </p>
      )}
      
      {/* Logs de debug */}
      <div className="share-logs" style={{ marginTop: "2rem", textAlign: "left", maxWidth: "800px", marginInline: "auto" }}>
        <h3>Logs (debug)</h3>
        <div style={{ 
          background: "#1e293b", 
          color: "#e2e8f0", 
          padding: "1rem", 
          borderRadius: "8px", 
          fontFamily: "monospace", 
          fontSize: "0.85rem",
          maxHeight: "300px",
          overflowY: "auto"
        }}>
          {logs.length === 0 ? "Aucun log pour le moment..." : logs.map((log, i) => (
            <div key={i}>{log}</div>
          ))}
        </div>
      </div>
      
      {rawPayload && (
        <div className="share-raw-payload" style={{ marginTop: "1.5rem", textAlign: "left", maxWidth: "800px", marginInline: "auto" }}>
          <h3>Contenu partagé (debug)</h3>
          <pre style={{ 
            whiteSpace: "pre-wrap", 
            wordBreak: "break-word", 
            background: "#f1f5f9", 
            padding: "1rem", 
            borderRadius: "8px",
            fontSize: "0.85rem"
          }}>
            {JSON.stringify(rawPayload, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default ShareReceiver;